import fetch from 'node-fetch';
import https from 'https';
import { ApolloClient, InMemoryCache, createHttpLink, HttpLink } from '@apollo/client';

const isBrowser = typeof window !== 'undefined';
const isPreview = String(process.env.PREVIEW_MODE).toLowerCase() === 'true';

// Function to get the appropriate CMS domain based on country code
function getCmsDomain() {
  // Default to AU CMS, but allow override via CMS environment variable
  return process.env.CMS || 'cms.jayco-com-au.staticlylocal.com';
}

// Create dynamic link function that gets CMS domain fresh each time
function createDynamicLink() {
  const cms = getCmsDomain();

  return new HttpLink({
    uri: `https://${cms}/graphql`,
    fetch: (uri, options) => {
      // Optional but highly recommended in Node for speed
      const agent = new https.Agent({
        keepAlive: true,
        maxSockets: 50,
        timeout: 60000,
      });
      
      console.log(`🔍 GraphQL Request to: ${uri}`);
      return fetch(uri, { ...options, agent });
    },
  });
}

// Initial link creation
const link = createDynamicLink();

// Cache config
const cache = new InMemoryCache();

// Enhanced default options for preview mode
const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
}

// Apollo Client factory function to create a client with the correct link
function createApolloClient() {
  const dynamicLink = createDynamicLink();
  
  return new ApolloClient({
    link: dynamicLink,
    cache,
    defaultOptions,
    // Add request debugging
    onError: ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.error(`🚨 GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`)
        );
      }
      if (networkError) {
        console.error(`🚨 Network error: ${networkError}`);
      }
      console.warn(`🔍 Failed operation: ${operation.operationName}`);
    },
  });
}

// Create the main client
const client = createApolloClient();

// Add query execution wrapper for debugging and dynamic link updates
const originalQuery = client.query.bind(client);
client.query = function(options) {
  // Refresh the link to ensure we're using the correct CMS based on current context
  const currentCms = getCmsDomain();
  const currentLink = client.link;
  
  // Check if we need to update the link (compare current CMS with the one in our link)
  const currentUri = currentLink.options?.uri;
  const expectedUri = `https://${currentCms}/graphql`;
  
  if (currentUri !== expectedUri) {
    console.log(`🔄 Updating GraphQL link from ${currentUri} to ${expectedUri}`);
    client.setLink(createDynamicLink());
  }
  
  const queryId = Math.random().toString(36).substr(2, 9);
  console.log(`🔍 Query ${queryId} starting:`, options.query.loc.source.body.substring(0, 100) + '...');
  
  return originalQuery(options).then(result => {
    console.log(`🔍 Query ${queryId} completed successfully:`, {
      hasData: !!result.data,
      dataKeys: Object.keys(result.data || {}),
      loading: result.loading
    });
    return result;
  }).catch(error => {
    console.error(`🔍 Query ${queryId} failed:`, error);
    throw error;
  });
};

export default client;
